import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { Session } from '@supabase/supabase-js';
import { adminApi, ApiError } from '../api.ts';
import { getAdminSupabase } from '../supabase.ts';
import type { AdminUser } from '../types.ts';
import { setAdminAccessToken } from './adminToken.ts';

type AdminAuthValue = {
  user: AdminUser | null;
  session: Session | null;
  loading: boolean;
  /** Sidebar-selected team region name; null = "All". Persisted per user. */
  activeTeamRegion: string | null;
  setActiveTeamRegion: (next: string | null) => void;
  signInWithPassword: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  reload: () => Promise<void>;
};

const AdminAuthContext = createContext<AdminAuthValue>({
  user: null,
  session: null,
  loading: true,
  activeTeamRegion: null,
  setActiveTeamRegion: () => {},
  signInWithPassword: async () => {},
  signOut: async () => {},
  reload: async () => {},
});

export function AdminAuthProvider({ children }: { children: ReactNode }): ReactNode {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTeamRegion, setActiveTeamRegionState] = useState<string | null>(null);

  const storageKey = (userId: string) => `l2u.activeTeamRegion.${userId}`;

  const setActiveTeamRegion = useCallback(
    (next: string | null) => {
      setActiveTeamRegionState(next);
      if (!user) return;
      try {
        if (next === null) localStorage.removeItem(storageKey(user.id));
        else localStorage.setItem(storageKey(user.id), next);
      } catch {
        // storage unavailable (private mode) — selection just won't persist
      }
    },
    [user],
  );

  const loadUser = useCallback(async (currentSession: Session | null) => {
    if (!currentSession) {
      setUser(null);
      setActiveTeamRegionState(null);
      return;
    }
    try {
      const res = await adminApi.get<{ user: AdminUser }>('/api/auth/admin/me');
      setUser(res.user);
      try {
        const stored = localStorage.getItem(`l2u.activeTeamRegion.${res.user.id}`);
        // A restricted admin's stored value must still be inside their
        // assignment; unrestricted admins accept any stored name (a
        // renamed/deleted region is reconciled by the shell's option list —
        // if the name no longer exists the shell resets to All).
        const assigned = res.user.team_regions.map((r) => r.name);
        if (stored && (assigned.length === 0 || assigned.includes(stored))) {
          setActiveTeamRegionState(stored);
        } else {
          setActiveTeamRegionState(null);
        }
      } catch {
        setActiveTeamRegionState(null);
      }
    } catch (err) {
      // 401/403 means the Supabase user has no matching public.users row —
      // not an admin. Clear the local Supabase session so they're not stuck.
      if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
        try {
          await getAdminSupabase().auth.signOut();
        } catch {
          // ignore
        }
        setUser(null);
        return;
      }
      setUser(null);
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    const supabase = (() => {
      try {
        return getAdminSupabase();
      } catch {
        return null;
      }
    })();

    if (!supabase) {
      setLoading(false);
      return () => {};
    }

    void supabase.auth.getSession().then(async ({ data }) => {
      if (!mounted) return;
      const initial = data.session ?? null;
      setSession(initial);
      setAdminAccessToken(initial?.access_token ?? null);
      await loadUser(initial);
      if (mounted) setLoading(false);
    });

    const { data: subscription } = supabase.auth.onAuthStateChange((_event, next) => {
      if (!mounted) return;
      setSession(next ?? null);
      setAdminAccessToken(next?.access_token ?? null);
      void loadUser(next ?? null);
    });

    return () => {
      mounted = false;
      subscription.subscription.unsubscribe();
    };
  }, [loadUser]);

  const signInWithPassword = useCallback(
    async (email: string, password: string) => {
      const supabase = getAdminSupabase();
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      setSession(data.session);
      setAdminAccessToken(data.session?.access_token ?? null);
      await loadUser(data.session);
    },
    [loadUser],
  );

  const signOut = useCallback(async () => {
    try {
      const supabase = getAdminSupabase();
      await supabase.auth.signOut();
    } finally {
      setSession(null);
      setUser(null);
      setAdminAccessToken(null);
      // Clear the in-memory selection only; the persisted per-user value
      // should survive re-login.
      setActiveTeamRegionState(null);
    }
  }, []);

  const reload = useCallback(async () => {
    await loadUser(session);
  }, [loadUser, session]);

  const value = useMemo<AdminAuthValue>(
    () => ({
      user,
      session,
      loading,
      activeTeamRegion,
      setActiveTeamRegion,
      signInWithPassword,
      signOut,
      reload,
    }),
    [
      user,
      session,
      loading,
      activeTeamRegion,
      setActiveTeamRegion,
      signInWithPassword,
      signOut,
      reload,
    ],
  );

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
}

export function useAdminAuth(): AdminAuthValue {
  return useContext(AdminAuthContext);
}
